import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useActiveClient } from "@/lib/activeClient";
import RecordingIndicator from "@/components/RecordingIndicator";
import NotificationBell from "@/components/NotificationBell";
import Sidebar from "@/components/Sidebar";
import SyncStatusBanner from "@/components/SyncStatusBanner";
import CommandK from "@/components/CommandK";
import KeyboardShortcutsHelp from "@/components/KeyboardShortcutsHelp";
import {
  LayoutDashboard, FileText, BarChart3, ClipboardList,
  Settings as SettingsIcon, Activity, ReceiptText, UserCog, LogOut, KeyRound, ChevronDown, ClipboardCheck,
  MessageSquare, Users, Mail, Mic, Stethoscope, CheckSquare, GraduationCap, MessageCircle, User, X, PenLine, Layers, Star, ShieldCheck, Trash2, Library,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { REPORT_PATHS } from "@/components/ReportsNav";
import { navItemActive } from "@/components/Sidebar";
import { useTheme } from "@/lib/theme/ThemeProvider";
import AppearancePanel from "@/components/appearance/AppearancePanel";
import PreviewBar from "@/components/appearance/PreviewBar";
import ConfirmAction from "@/components/appearance/ConfirmAction";
import { useUiPref } from "@/lib/uiPrefs";
import { Palette } from "lucide-react";

// Today's work — the "why did I open the app?" surface.
const todayNav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard", roles: ["owner", "manager"] },
  { to: "/schedule", label: "Schedule", icon: ClipboardList, testId: "nav-schedule", roles: ["owner", "manager", "staff"] },
  { to: "/tasks", label: "Tasks", icon: CheckSquare, testId: "nav-tasks", roles: ["owner", "manager", "staff", "practitioner", "administration", "auditor"] },
];

// Client care — everything the client's record touches.
const clientCareNav = [
  { to: "/clients", label: "Clients", icon: Users, testId: "nav-clients", roles: ["owner", "manager", "staff", "practitioner", "administration", "auditor"] },
  { to: "/assessments", label: "Assessments", icon: Stethoscope, testId: "nav-assessments", roles: ["owner", "manager", "staff"] },
  { to: "/progress-review", label: "Progress Review", icon: ClipboardCheck, testId: "nav-checkins", roles: ["owner", "manager", "staff", "practitioner", "administration", "auditor"] },
  { to: "/resource-library", label: "Resource Library", icon: Library, testId: "nav-resource-library", roles: ["owner", "manager", "staff", "practitioner", "administration", "auditor"] },
  { to: "/writing", label: "Writing", icon: PenLine, testId: "nav-writing", roles: ["owner", "manager"] },
  { to: "/letters", label: "Letters", icon: Mail, testId: "nav-letters", roles: ["owner", "manager"] },
  { to: "/intake-forms", label: "Onboarding", icon: ClipboardCheck, testId: "nav-intake" },
  { to: "/sbn", label: "SbN", icon: Activity, testId: "nav-sbn", roles: ["owner", "manager", "staff"] },
  { to: "/learning", label: "Learning", icon: GraduationCap, testId: "nav-learning", roles: ["owner", "manager", "staff", "practitioner", "administration", "auditor"] },
];

// Follow-up actions — what happens after the appointment ends.
const followUpNav = [
  { to: "/enquiries", label: "Enquiries", icon: MessageCircle, testId: "nav-enquiries", roles: ["owner", "manager", "staff", "practitioner", "administration", "auditor"] },
  { to: "/reviews", label: "Reviews", icon: Star, testId: "nav-reviews", roles: ["owner", "manager"] },
  { to: "/reconciliation", label: "Receipts", icon: ReceiptText, testId: "nav-reconciliation", roles: ["owner", "manager", "staff"] },
  { to: "/messaging", label: "Messaging", icon: MessageSquare, testId: "nav-messaging", roles: ["owner", "manager"] },
];

// Manage — commercial/admin, less-frequent surfaces. Program-library
// tools (Doc Imports, Bulk Programs, and the combined Blocks + Templates
// library) live here because they're clinic-wide curation surfaces,
// not per-client work.
const manageNav = [
  { to: "/document-imports", label: "Doc Imports", icon: FileText, testId: "nav-document-imports", roles: ["owner", "manager", "staff", "practitioner", "administration"] },
  { to: "/bulk-program-import", label: "Bulk Programs", icon: FileText, testId: "nav-bulk-program-import", roles: ["owner", "manager", "practitioner"] },
  { to: "/program-library", label: "Program Library", icon: Layers, testId: "nav-program-library", roles: ["owner", "manager", "practitioner"] },
  // Reports hub: Business reports, Geronimo KPIs, Appointments, Product orders, Products, Payroll bonus.
  { to: "/reports", label: "Reports", icon: BarChart3, testId: "nav-reports", roles: ["owner", "manager"], match: REPORT_PATHS },
  { to: "/governance", label: "Governance", icon: ShieldCheck, testId: "nav-governance", roles: ["owner", "manager"] },
  { to: "/deleted-items", label: "Deleted Items", icon: Trash2, testId: "nav-deleted-items", roles: ["owner", "manager"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings", roles: ["owner", "manager"] },
];

const STATUS_CACHE_KEY = "acuity-reports:status";

function loadCachedStatus() {
  try {
    const raw = localStorage.getItem(STATUS_CACHE_KEY);
    if (!raw) return { configured: false, user_id: null };
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed;
  } catch (e) { /* ignore */ }
  return { configured: false, user_id: null };
}

function saveCachedStatus(s) {
  try {
    localStorage.setItem(STATUS_CACHE_KEY, JSON.stringify(s));
  } catch (e) { /* ignore */ }
}

function initials(name, email) {
  const src = (name || email || "").trim();
  if (!src) return "?";
  const parts = src.split(/[\s@.]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function roleTone(role) {
  if (role === "owner") return "bg-zinc-950 text-white";
  if (role === "manager") return "bg-emerald-100 text-emerald-800 border border-emerald-200";
  return "bg-zinc-100 text-zinc-800 border border-zinc-200";
}

export default function Layout() {
  const [status, setStatus] = useState(loadCachedStatus);
  const [recoveryCount, setRecoveryCount] = useState(0);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { client: activeClient, clearClient } = useActiveClient();
  const theme = useTheme();
  const { pathname } = useLocation();
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  // Reports menu reopens the last report tab used (saved to the account).
  const [lastReport, setLastReport] = useUiPref("last_report_tab", "/reports");
  const canSeeReports = user?.role === "owner" || user?.role === "manager";
  useEffect(() => {
    if (canSeeReports && REPORT_PATHS.includes(pathname) && pathname !== lastReport) setLastReport(pathname);
  }, [pathname, canSeeReports]); // eslint-disable-line react-hooks/exhaustive-deps

  const role = user?.role;
  const filterByRole = (items) =>
    items.filter((it) => !it.roles || !role || it.roles.includes(role));

  const flatNav = filterByRole(todayNav);
  const clientCareItems = filterByRole(clientCareNav);
  const followUpItems = filterByRole(followUpNav);
  const manageItems = (() => {
    const items = filterByRole(manageNav).map((it) =>
      it.match === REPORT_PATHS ? { ...it, to: REPORT_PATHS.includes(lastReport) ? lastReport : "/reports" } : it
    );
    if (role === "owner") {
      items.push({ to: "/users", label: "Users", icon: UserCog, testId: "nav-users" });
    }
    return items;
  })();

  const refreshStatus = () =>
    api
      .get("/settings")
      .then((r) => {
        setStatus(r.data);
        saveCachedStatus(r.data);
      })
      .catch(() => {});

  useEffect(() => {
    refreshStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll the incomplete-assessments count every 60s so the recovery
  // badge in the header stays fresh without a page reload.
  useEffect(() => {
    let cancelled = false;
    async function tick() {
      try {
        const r = await api.get("/soap-notes/sessions/incomplete");
        if (!cancelled) setRecoveryCount(r.data?.count || 0);
      } catch { /* ignore — badge just stays as-is */ }
    }
    tick();
    const id = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  async function onSignOut() {
    if (theme.dirty && !confirmSignOut) {
      setConfirmSignOut(true);
      return;
    }
    setConfirmSignOut(false);
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="min-h-screen flex flex-col bg-le-page">
      <header className="sticky top-0 z-50 border-b border-zinc-200 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto max-w-[1400px] px-6 sm:px-8 md:px-10 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2.5 group"
            data-testid="brand-link"
          >
            <div className="h-8 w-8 grid place-items-center bg-zinc-950 text-white rounded-md">
              <Activity className="h-4 w-4" strokeWidth={2.25} />
            </div>
            <div className="font-display text-base sm:text-lg font-bold tracking-tight text-zinc-950 whitespace-nowrap">
              Living Energy <span className="text-zinc-400">·</span> Hub
            </div>
          </button>

          {/* Desktop primary nav lives in the left sidebar (see Sidebar.jsx).
              The mobile top-strip below covers small viewports. */}

          <div className="flex items-center gap-3">
            {activeClient && (
              <div
                className="hidden md:inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full border border-zinc-300 bg-zinc-50 text-xs text-zinc-800 hover:border-zinc-400 transition-colors"
                data-testid="active-client-badge"
                title={`Currently working on ${activeClient.name || activeClient.email}`}
              >
                <button
                  onClick={() => navigate(`/clients/${encodeURIComponent(activeClient.email)}${activeClient.canonical_client_id ? `?as=${encodeURIComponent(activeClient.canonical_client_id)}` : ""}`)}
                  className="flex items-center gap-1.5 pr-1 hover:text-zinc-950"
                  data-testid="active-client-jump"
                >
                  <User className="h-3.5 w-3.5 text-zinc-500" />
                  <span className="max-w-[160px] truncate font-medium">
                    {activeClient.name || activeClient.email}
                  </span>
                </button>
                <button
                  onClick={clearClient}
                  className="h-5 w-5 grid place-items-center rounded-full hover:bg-zinc-200 text-zinc-500 hover:text-zinc-800"
                  data-testid="active-client-clear"
                  title="Clear active client"
                  aria-label="Clear active client"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            {recoveryCount > 0 && (
              <button
                onClick={() => navigate("/assessments/recovery")}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-xs font-medium hover:bg-amber-100 transition-colors"
                data-testid="recovery-badge"
                title="Interrupted assessment recordings need attention"
              >
                <Mic className="h-3.5 w-3.5" />
                <span className="tabular-nums">{recoveryCount}</span>
                <span className="hidden lg:inline">interrupted</span>
              </button>
            )}
            <div
              className={cn(
                "hidden sm:flex items-center gap-2 px-2.5 py-1.5 rounded-md border text-xs font-medium",
                status.configured
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-amber-200 bg-amber-50 text-amber-700"
              )}
              data-testid="connection-status-badge"
            >
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full",
                  status.configured ? "bg-emerald-500" : "bg-amber-500"
                )}
              />
              {status.configured ? "Acuity connected" : "Not connected"}
            </div>

            {user && (
              <button
                type="button"
                onClick={() => theme.setPanelOpen(!theme.panelOpen)}
                className={cn(
                  "inline-flex items-center gap-1.5 h-8 px-2 rounded-md border text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-1",
                  theme.panelOpen ? "border-le-primary bg-le-selected text-le-primary-deep" : "border-zinc-200 text-zinc-600 hover:text-zinc-950 hover:bg-zinc-50"
                )}
                title="Appearance — preview themes on this page"
                aria-label="Appearance"
                aria-expanded={theme.panelOpen}
                data-testid="appearance-panel-trigger"
              >
                <Palette className="h-4 w-4" />
                <span className="hidden xl:inline">Appearance</span>
              </button>
            )}

            {user && <NotificationBell />}

            {/* Global search discoverability affordance — clicking it
                triggers the same ⌘K keyboard shortcut, so keyboard-
                free operators still know the feature exists. */}
            {user && (
              <button
                type="button"
                onClick={() => {
                  window.dispatchEvent(new KeyboardEvent("keydown",
                    { key: "k", metaKey: true }));
                }}
                className="hidden md:inline-flex items-center gap-1.5 h-8 px-2 rounded-md border border-zinc-200 text-xs text-zinc-600 hover:text-zinc-950 hover:bg-zinc-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-1"
                title="Search everything (⌘K / Ctrl+K)"
                data-testid="global-search-trigger"
              >
                <span className="text-zinc-600">Search…</span>
                <kbd className="text-2xs px-1 py-0.5 border border-zinc-300 rounded bg-zinc-50 text-zinc-700">
                  ⌘K
                </kbd>
              </button>
            )}

            {user && (
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event("open-shortcuts-help"))}
                className="hidden lg:inline-flex items-center gap-1 h-8 w-8 justify-center rounded-md border border-zinc-200 text-zinc-600 hover:text-zinc-950 hover:bg-zinc-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-1"
                title="Keyboard shortcuts (press ?)"
                aria-label="Show keyboard shortcuts"
                data-testid="shortcuts-help-trigger"
              >
                <kbd className="text-2xs font-semibold text-zinc-700">?</kbd>
              </button>
            )}

            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="user-menu-trigger"
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-zinc-100 transition-colors"
                  >
                    <div className="h-7 w-7 grid place-items-center rounded-full bg-zinc-200 text-zinc-800 text-xs font-semibold font-mono-num">
                      {initials(user.name, user.email)}
                    </div>
                    <Badge className={cn("hidden sm:inline-flex text-2xs uppercase tracking-wider", roleTone(user.role))}>
                      {user.role}
                    </Badge>
                    <ChevronDown className="h-3.5 w-3.5 text-zinc-500" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="font-medium text-zinc-950">{user.name || "—"}</div>
                    <div className="text-xs text-zinc-500 font-mono-num truncate">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {user.role === "owner" && (
                    <DropdownMenuItem onClick={() => navigate("/users")} data-testid="menu-users-link">
                      <UserCog className="h-4 w-4 mr-2" /> Users & roles
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate("/appearance")} data-testid="menu-appearance">
                    <Palette className="h-4 w-4 mr-2" /> Appearance
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/change-password")} data-testid="menu-change-password">
                    <KeyRound className="h-4 w-4 mr-2" /> Change password
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onSignOut} data-testid="menu-sign-out" className="text-red-700 focus:text-red-800">
                    <LogOut className="h-4 w-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        {/* Mobile nav — flatten all groups so nothing hides */}
        <div className="md:hidden border-t border-zinc-200 px-3 py-2 flex gap-1 overflow-x-auto thin-scroll">
          {[...flatNav, ...clientCareItems, ...followUpItems, ...manageItems].map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={`${it.testId}-mobile`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-1.5 px-3 py-1.5 text-xs whitespace-nowrap rounded-md",
                  navItemActive(it, isActive, pathname)
                    ? "bg-zinc-950 text-white"
                    : "text-zinc-600 hover:bg-zinc-100"
                )
              }
            >
              <it.icon className="h-3.5 w-3.5" strokeWidth={2} />
              {it.label}
            </NavLink>
          ))}
        </div>
      </header>

      <div className="flex-1 flex w-full">
        <Sidebar
          sections={[
            { key: "today", label: "Today's work", items: flatNav },
            { key: "client-care", label: "Client care", items: clientCareItems },
            { key: "follow-up", label: "Follow-up", items: followUpItems },
            { key: "manage", label: "Manage", items: manageItems },
          ]}
        />
        <main className="flex-1 w-full min-w-0 mx-auto max-w-[1400px] px-6 sm:px-8 md:px-10 py-8 md:py-12">
          <SyncStatusBanner />
          <Outlet context={{ status, refreshStatus, user }} />
        </main>
      </div>

      <footer className="border-t border-zinc-200 py-6">
        <div className="mx-auto max-w-[1400px] px-6 sm:px-8 md:px-10 text-xs text-zinc-600 flex items-center justify-between">
          <span>Living Energy Hub · Clinic workspace</span>
          <span className="font-mono-num">v1.0</span>
        </div>
      </footer>
      <RecordingIndicator
        onOpen={(s) => navigate(s?.initiating_assessment_id ? `/assessments/${s.initiating_assessment_id}` : "/intake-forms")}
      />
      <CommandK />
      <KeyboardShortcutsHelp />
      <AppearancePanel />
      <PreviewBar />
      <ConfirmAction
        open={confirmSignOut}
        onOpenChange={setConfirmSignOut}
        testId="signout-theme-confirm"
        title="Discard your theme preview?"
        description="You have an unsaved appearance preview. Signing out will discard it. Nothing else is affected."
        confirmLabel="Sign out"
        onConfirm={onSignOut}
      />
    </div>
  );
}
